import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FilterStateItem } from "@racoon/ui";
import { ConnectProto, SelectOption } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { isShiftPlanned, isShiftTerminal } from "../helpers";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { toTimestamp, normalizeForCompactSearch } from "@racoon/lib";

/** ---- constants ---- */
const SHIFT_STATES: FilterStateItem[] = [{ key: "Все", value: "all" }];

const fmtYMD = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fmtISO = (d: Date) => d.toISOString();
const asStringOptions = (xs?: SelectOption[]) =>
  (xs ?? [])
    .filter(({ id, name }) => id && name && String(id).trim() && name.trim())
    .map(({ id, name }) => ({ id: String(id), name }));

/** Maps a filter option onto the statuses it covers. */
export const matchesStatus = (shift: ConnectProto.DriverOnShift, option: string) => {
  const { status } = shift;
  switch (option) {
    case "planned":
      return isShiftPlanned(status);
    case "completed":
      return status === ConnectProto.DriverOnShiftStatus.ENDED && !isTtnApproved(shift);
    case "ttnApproved":
      return isTtnApproved(shift);
    case "cancelled":
      return status === ConnectProto.DriverOnShiftStatus.CANCELLED;
    case "active":
      return !isShiftPlanned(status) && !isShiftTerminal(status);
    default:
      return true;
  }
};

/** ---- options ---- */
type Options = {
  /** Options for building FilterBlock selects */
  options?: {
    drivers?: SelectOption[];
    trucks?: SelectOption[];
  };
  /** Raw array returned by API (unfiltered) */
  data?: ConnectProto.DriverOnShift[] | undefined;
  /** Organization ID for filtering - required parameter */
  ownerOrganizationId: number;
};

export const useShiftsUrlState = (opts: Options) => {
  const [sp, setSp] = useSearchParams();
  const t = useT();

  /** ---------- BATCHED PATCHER (coalesces same-tick updates) ---------- */
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // freeze defaults on mount
  const todayRef = useRef(new Date());
  const defaultStartRef = useRef(
    new Date(
      todayRef.current.getFullYear(),
      todayRef.current.getMonth(),
      todayRef.current.getDate(),
    ),
  );

  // URL params (strings)
  const statusS = sp.get("status") ?? "all";
  const startS = sp.get("start");
  const endS = sp.get("end");
  const driverId = sp.get("driverId") ?? "";
  const truckId = sp.get("truckId") ?? "";
  // `status` is taken by the pill row above, so the sheet's filter has its own key.
  const shiftStatus = sp.get("shiftStatus") ?? "";
  const searchValue = sp.get("search") ?? "";

  // stable strings for API
  const startYmd = useMemo(() => startS ?? fmtYMD(defaultStartRef.current), [startS]);
  const endYmd = useMemo(() => {
    if (endS) return endS;
    const d = new Date(todayRef.current);
    d.setHours(23, 59, 59, 999);
    return fmtISO(d);
  }, [endS]);

  // UI dates for pickers
  const startDate = useMemo(() => new Date(startYmd), [startYmd]);
  const endDate = useMemo(() => new Date(endYmd), [endYmd]);

  const selectedState = SHIFT_STATES.find((s) => s.value === statusS) ?? SHIFT_STATES[0];

  /** ----- URL-driven handlers ----- */
  const onStateChange = (v: FilterStateItem) => patch({ status: v.value });

  const onDateChange = (k: "startDate" | "endDate", d: Date) => {
    const date = new Date(d);
    if (k === "startDate") {
      // Set time to start of day (00:00:00)
      date.setHours(0, 0, 0, 0);
    } else {
      // Set time to end of day (23:59:59)
      date.setHours(23, 59, 59, 999);
    }
    patch({ [k === "startDate" ? "start" : "end"]: fmtISO(date) });
  };

  const onFilterParamsChange = (key: string, value: string) =>
    patchMany({ [key]: value === "all" ? "" : value });

  const onSearchChange = (value: string) => patch({ search: value });

  // RTKQ query (stable primitives only)
  const apiQuery = useMemo(() => {
    const fromDate = new Date(startYmd);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(endYmd);
    toDate.setHours(23, 59, 59, 999);

    const q = {
      ownerOrganizationIds: [BigInt(opts.ownerOrganizationId)],
      dateTimeFilter: {
        from: toTimestamp(fromDate),
        to: toTimestamp(toDate),
        fields: ["started_at", "tw_start_at"],
      },
    };
    return q;
  }, [startYmd, endYmd, opts.ownerOrganizationId]);

  // FilterBlock params
  const filterParams = useMemo(
    () => [
      {
        key: "driverId",
        value: driverId,
        type: "select" as const,
        searchable: true,
        label: t(Namespace.Autopark, "filters.driver"),
        options: asStringOptions(opts.options?.drivers),
      },
      {
        key: "truckId",
        value: truckId,
        type: "select" as const,
        searchable: true,
        searchMatch: "compact" as const,
        label: t(Namespace.Autopark, "filters.plate"),
        options: asStringOptions(opts.options?.trucks),
      },
      {
        key: "shiftStatus",
        value: shiftStatus,
        type: "select" as const,
        label: t(Namespace.Autopark, "table.shifts.status"),
        // Grouped the way the badge groups them, so the options read as the
        // statuses the table actually shows rather than every enum value.
        options: [
          { id: "active", name: t(Namespace.Autopark, "shifts.status.active") },
          { id: "planned", name: t(Namespace.Autopark, "shifts.status.notApproved") },
          { id: "completed", name: t(Namespace.Autopark, "shifts.status.completed") },
          { id: "ttnApproved", name: t(Namespace.Autopark, "shifts.status.ttnApproved") },
          { id: "cancelled", name: t(Namespace.Autopark, "shifts.status.cancelled") },
        ],
      },
    ],
    [driverId, truckId, shiftStatus, opts.options, t],
  );

  // client-side filtering + totals
  const { items, total, filteredTotal } = useMemo(() => {
    const all = opts.data ?? [];
    const totalAll = all.length;
    const term = normalizeForCompactSearch(searchValue);

    // Driver name, shift id (with or without the "S" the table shows) and plate.
    const matchesSearch = (shift: ConnectProto.DriverOnShift) => {
      const haystacks = [
        `${shift.driver?.firstName ?? ""} ${shift.driver?.lastName ?? ""}`,
        `s${shift.id ?? ""}`,
        shift.truck?.plateNumber ?? "",
      ];
      return haystacks.some((h) => normalizeForCompactSearch(h).includes(term));
    };

    const matches = (shift: ConnectProto.DriverOnShift) => {
      if (driverId && driverId !== "") {
        const dId = shift.driver?.id != null ? String(shift.driver.id) : "";
        if (dId !== driverId) return false;
      }
      if (truckId && truckId !== "") {
        const tId = shift.truck?.id != null ? String(shift.truck.id) : "";
        if (tId !== truckId) return false;
      }
      if (shiftStatus && !matchesStatus(shift, shiftStatus)) return false;
      if (term && !matchesSearch(shift)) return false;
      return true;
    };

    const filtered = all.filter(matches);
    return { items: filtered, total: totalAll, filteredTotal: filtered.length };
  }, [opts.data, driverId, truckId, shiftStatus, searchValue]);

  return {
    // url-driven state
    states: SHIFT_STATES,
    selectedState,
    startDate,
    endDate,
    onStateChange,
    onFilterParamsChange,
    onDateChange,
    searchValue,
    onSearchChange,
    url: { driverId, truckId, shiftStatus, search: searchValue },

    // api + filter descriptors
    apiQuery,
    filterParams,

    // results of front filtering
    items,
    total,
    filteredTotal,
  };
};
